import { useMemo } from "react"
import { Alert, Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { skillMapLines as skillMapLinesController } from "routes/skill-mapper/controller/skill-map-lines"
import { getCurrencySymbol } from "../format-utils"
import { SkillOverrideRow } from "./skill-override-row"

export function RateCardSkillOverrides({ line, skillMapId, currency, onChange, readOnly }) {
    const allSkillMapLines = skillMapLinesController.useResults({ skillMapId }) ?? []
    const symbol = getCurrencySymbol(currency)
    const mappedSkills = useMemo(getMappedSkills, [allSkillMapLines, line.tradeId])
    const overrides = line.skillOverrides || []
    const mappedSkillIdSet = new Set(mappedSkills.map(toSkillIdString))
    const orphaned = overrides.filter(isOrphaned)
    const orphanedMessage = getOrphanedMessage()

    return (
        <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }} data-cy="skill-overrides-panel">
            <Stack spacing={1.5}>
                <Typography variant="caption" color="text.secondary">
                    Skill-specific rates override the trade's default hourly rate for tasks with a matching skill.
                </Typography>
                <If value={mappedSkills.length === 0}>
                    <Typography variant="body2" color="text.secondary">
                        No skills mapped to this trade yet.
                    </Typography>
                </If>
                <Repeat
                    list={mappedSkills}
                    item={
                        <SkillOverrideRow
                            overrides={overrides}
                            symbol={symbol}
                            onChange={handleChange}
                            readOnly={readOnly}
                        />
                    }
                />
                <If value={orphaned.length > 0}>
                    <Alert severity="warning" data-cy="orphaned-overrides" action={renderOrphanedAction()}>
                        {orphanedMessage}
                    </Alert>
                </If>
            </Stack>
        </Box>
    )

    function getMappedSkills() {
        return (allSkillMapLines || [])
            .filter((l) => l.tradeId === line.tradeId)
            .sort((a, b) => (a.skillName || a.skillId).localeCompare(b.skillName || b.skillId))
    }

    function toSkillIdString(s) {
        return String(s.skillId)
    }

    function isOrphaned(o) {
        return !mappedSkillIdSet.has(String(o.skillId))
    }

    function isMapped(o) {
        return mappedSkillIdSet.has(String(o.skillId))
    }

    function handleChange(skillId, rate) {
        if (readOnly) return
        const filtered = overrides.filter(notMatchingSkill)
        const next = rate == null ? filtered : [...filtered, { skillId, hourlyRate: rate }]
        onChange(next)

        function notMatchingSkill(o) {
            return String(o.skillId) !== String(skillId)
        }
    }

    function handleRemoveOrphaned() {
        if (readOnly) return
        const next = overrides.filter(isMapped)
        onChange(next)
    }

    function renderOrphanedAction() {
        if (readOnly) return null
        return (
            <Tooltip title="Remove orphaned overrides" placement="top">
                <IconButton size="small" onClick={handleRemoveOrphaned}>
                    <Iconify icon="eva:trash-2-outline" width={20} />
                </IconButton>
            </Tooltip>
        )
    }

    function getOrphanedMessage() {
        if (orphaned.length === 1) return "1 override belongs to a skill no longer mapped to this trade."
        return `${orphaned.length} overrides belong to skills no longer mapped to this trade.`
    }
}
